import { useEffect, useState } from 'react'
import { RefreshCw, TriangleAlert } from 'lucide-react'
import { api } from '../../api/client'
import ErrorBanner from '../../components/ErrorBanner'
import { useToast } from '../../components/Toast.jsx'

const ACTIONS = { truncate: 'Delete all', delete_non_admins: 'Delete non-admins' }

/** Wipes the company's records to start again, after showing what will go. */
export function ResetDataCard() {
  const [plan, setPlan] = useState([])
  const [loading, setLoading] = useState(true)
  const [errMsg, setErrMsg] = useState('')
  const [confirm, setConfirm] = useState('')
  const [doBackup, setDoBackup] = useState(true)
  const [clearSettings, setClearSettings] = useState(false)
  const [result, setResult] = useState(null)
  const { notify } = useToast()

  const loadPlan = async (clear = clearSettings) => {
    setErrMsg(''); setLoading(true)
    try {
      const params = { fast: 1 }
      if (clear) params.clear_settings = 1
      const { data } = await api.get('/maintenance/reset-plan', { params })
      setPlan(data?.plan || [])
    } catch (e) { setErrMsg(e.message || 'Couldn’t load what a reset would delete.') }
    setLoading(false)
  }

  useEffect(() => { loadPlan() }, [])

  const runReset = async () => {
    setErrMsg(''); setResult(null)
    try {
      const { data } = await api.post('/maintenance/reset', { confirm, backup: doBackup, clear_settings: clearSettings })
      setResult(data || {})
      notify('Data reset')
      if (data?.backup_path) notify('Backup saved')
      setConfirm('')
      await loadPlan()
    } catch (e) { setErrMsg(e.message || 'The reset didn’t finish.') }
  }

  const rowsOf = (p) => (p.action === 'delete_non_admins' ? (p.delete_rows || 0) : (p.rows || 0))
  const totalWillDelete = plan.reduce((s, p) => s + (p.action === 'truncate' || p.action === 'delete_non_admins' ? rowsOf(p) : 0), 0)

  return (
    <div className="ci-form-meta-card mf-form set-danger" id="reset">
      <div className="mf-card-title set-card-title"><TriangleAlert size={16} />Reset data</div>
      <div className="set-body">
        {errMsg && <ErrorBanner message={errMsg} />}
        <p>Deletes your company’s records so you can start again. This can’t be undone.</p>
        <ul className="set-list">
          <li>Sales, purchases, stock, accounting entries, and master files such as products, customers and vendors are deleted.</li>
          <li>Admin users are kept. Other users are deleted.</li>
          <li>With a backup, a copy of your data is saved first. Only your admins can download it.</li>
          <li>Your company profile and settings are kept, unless you tick the box below.</li>
        </ul>

        <div className="set-checks">
          <label className="ci-form-toggle mf-check"><input type="checkbox" checked={doBackup} onChange={(e) => setDoBackup(e.target.checked)} />Save a backup first</label>
          <label className="ci-form-toggle mf-check"><input type="checkbox" checked={clearSettings} onChange={(e) => { setClearSettings(e.target.checked); loadPlan(e.target.checked) }} />Also clear the company profile and settings</label>
        </div>

        <details className="set-plan">
          <summary>
            What will be deleted: {loading ? 'checking…' : `about ${totalWillDelete.toLocaleString()} rows in ${plan.length} tables`}
            <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={(e) => { e.preventDefault(); loadPlan() }} disabled={loading}><RefreshCw size={13} />Check again</button>
          </summary>
          <div className="ci-table-scroll">
            <table className="ci-table">
              <thead><tr><th>Table</th><th>What happens</th><th className="num">Rows</th></tr></thead>
              <tbody>
                {plan.map((p, i) => (
                  <tr key={i}><td>{p.table}</td><td>{ACTIONS[p.action] || p.action}</td><td className="num">{rowsOf(p).toLocaleString()}</td></tr>
                ))}
                {plan.length === 0 && <tr><td colSpan="3"><div className="ci-empty">Nothing to delete.</div></td></tr>}
              </tbody>
            </table>
          </div>
        </details>

        <div className="set-confirm">
          <label className="ci-form-field">
            <span>Type RESET to confirm</span>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="RESET" autoComplete="off" />
          </label>
          <button
            type="button"
            className="ci-btn ci-btn-danger-solid"
            disabled={confirm !== 'RESET'}
            onClick={() => {
              if (confirm !== 'RESET') return
              if (!window.confirm('Reset your company’s data? This can’t be undone.')) return
              runReset()
            }}
          >Reset data</button>
        </div>

        {result && (
          <div className="set-result">
            {result.backup_path
              ? <p>Backup: <a href={result.backup_path} download>download the backup</a></p>
              : <p className="doc-hint">No backup was saved.</p>}
            {(result.results || []).length > 0 && (
              <div className="ci-table-scroll">
                <table className="ci-table">
                  <thead><tr><th>Table</th><th>What happened</th><th>Result</th><th className="num">Rows before</th><th className="num">Rows deleted</th></tr></thead>
                  <tbody>
                    {result.results.map((r, i) => (
                      <tr key={i}><td>{r.table}</td><td>{ACTIONS[r.action] || r.action}</td><td>{r.ok ? 'Done' : 'Failed'}</td><td className="num">{r.rows_before ?? ''}</td><td className="num">{r.rows_deleted ?? ''}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
